// Einsicht in Gesendetes (E2, Schritt 4): Der Verkäufer behält alles, was
// er gesendet hat, zum Nachsehen (Gernot, 19.10.2026). Texte und Längen
// bleiben am Gerät als Nur-lesen-Kopie, Fotos, Memos und das K2-PDF holt die
// App bei Bedarf vom Server (dateien.ts, `dateiZumAnsehen`).
//
// Der Abgleich mit dem Server (`einsichtAbgleichen`) – beim Öffnen der
// Startseite und wenn das Netz zurückkommt, höchstens etwa einmal je Minute:
//  - GET /api/doku/aufnahmen: die eigenen gesendeten Besichtigungen.
//  - Fehlt eine davon am Gerät (anderes Gerät, App neu installiert), holt
//    die App sie (GET /api/doku/aufnahme/:id) und legt sie als Nur-lesen-
//    Kopie ab – höchstens die 20 neuesten auf einmal, der Rest beim nächsten
//    Mal.
//  - Steht eine Kopie „im Büro“ am Gerät nicht mehr in der Liste, wird sie
//    vorgemerkt (`vermisstSeit`); fehlt sie drei Tage lang bei jedem
//    Abgleich, kommt sie auch am Gerät weg – samt Stationen, Notizen,
//    Längen, Fotos, Memos, Dachplanung und Dateien. Taucht sie vorher wieder
//    auf, ist die Vormerkung weg. Warum die Frist (Code-Prüfung E2,
//    Befund 2, 22.10.2026): Der Server hat keinen eigenen Weg, ein Löschen
//    (Löschregel O19) zu melden – „fehlt in der Liste“ kann auch eine
//    Wiederherstellung aus einer älteren Sicherung sein, und dann wäre die
//    Kopie am Gerät die letzte Spur.
//  - Eine Antwort ohne Liste (leerer Körper, fremdes JSON mit Status 200 –
//    etwa ein WLAN-Anmeldeportal) ist KEINE leere Liste: Dann wird nichts
//    abgeglichen (Code-Prüfung E2, Befund 1).
//  - Nie angerührt wird eine Besichtigung, die noch aufgenommen wird oder
//    auf dem Weg ins Büro ist – und vorgemerkt oder gelöscht wird nur nach
//    einer erfolgreichen Antwort. Seit Schritt 5 ebenso nie eine, an der
//    gerade etwas nachgereicht wird (ein Nachtrag offen oder auf dem Weg).
//  - Die Kopie trägt, was nachgereicht wurde (seit Schritt 5): Stücke mit
//    ihrem Nachtrag, berichtigte Längen und „Sonstiges“ mit der Marke, die
//    Nachträge, die im Büro sind. Was zu einem Nachtrag gehört, der am
//    Server noch offen ist (auf einem anderen Gerät begonnen), gehört dorthin
//    – es kommt nicht in die Kopie.
// Fehler bleiben still: ohne Empfang ist das draußen der Normalfall. Nur
// Unerwartetes geht an die Fehlerwache, ohne Inhalte.
//
// Seit E3 (S6, S7) trägt die Kopie auch die Fragen samt Antworten und den
// Montagetermin (gespraech.ts). Die ändern sich nach dem Senden – das Büro
// fragt, antwortet, setzt „erledigt“, trägt den Termin ein –, deshalb legt
// der Abgleich nicht mehr nur fehlende Kopien an, er frischt auch auf
// (`kopieAuffrischen`: Fragen, Termin, Name und Adresse – nie die Stücke der
// Besichtigung, die ändert nur ein Nachtrag vom Gerät):
//  - beim Abgleich jede Kopie, deren Zahl offener Fragen laut Liste nicht
//    mehr die ist, die sie beim letzten Auffrischen hatte (eine neue Frage,
//    eine erledigte) – und eine mit offenen Fragen, wenn sie älter als fünf
//    Minuten ist (eine neue Antwort ändert die Zahl nicht). Warum nicht alle:
//    Eine Anfrage je Besichtigung bei jedem Abgleich wäre mit hunderten
//    Kopien zu viel; die Liste sagt, wo sich etwas tut (PLAN.md, E3,
//    „Handwerk“: kein eigener Abgleich für die Fragen);
//  - beim Öffnen einer gesendeten Besichtigung (`auffrischenAnstossen`) –
//    was man ansieht, soll stimmen, auch der Termin, den das Büro geändert
//    hat.
// Seit E3 gibt der Abgleich außerdem nur noch die Dateien der Kopien frei,
// an denen wirklich noch Bytes hängen (O55, dateien.ts).
//
// Seit E3c (S6; O53, Prüfung des Plans E3c, Befund 2): Die Liste nennt
// unter `geloescht`, was das Büro gelöscht hat – auch nie Gesendetes. Dazu
// räumt der Abgleich die Kopie sofort weg, in jedem Stand, samt Postausgang
// (wegraeumen.ts, vomBueroGeloescht), und die Startseite sagt es einmal.
// „Fehlt in der Liste“ mit der Drei-Tage-Frist bleibt als Rückfall (ein
// Server mit altem Stand, eine Wiederherstellung aus der Sicherung).

import { istKennung, type AufnahmeAmServer, type GesendetAntwort, type GesendetZeile } from "@happy-energy/kern";
import { ApiFehler, fehlerMelden, holen, OhneVerbindung, ohneNetz } from "@happy-energy/oberflaeche";
import {
  speicher,
  stueckeTabellen,
  type Aufnahme,
  type Foto,
  type K2,
  type K2Excel,
  type Laenge,
  type Materialangabe,
  type Memo,
  type Nachtrag,
  type Notiz,
  type Station,
} from "./speicher.ts";
import { aufnahmenMitDateien, dateienFreigeben } from "./dateien.ts";
import { eigenesAufraeumen } from "./gespraech.ts";
import { besichtigungWegraeumen, vomBueroGeloescht, wegraeumTabellen } from "./wegraeumen.ts";

/** Höchstens so oft gleicht die Startseite mit dem Server ab. */
export const EINSICHT_TAKT = 60_000;
/** So viele fehlende Kopien holt ein Abgleich höchstens – und so viele frischt er höchstens auf. */
export const KOPIEN_JE_ABGLEICH = 20;
/** So lange bleibt eine Kopie am Gerät, die der Server nicht mehr nennt, bevor sie gelöscht wird (Befund 2 der Code-Prüfung E2). */
export const KOPIE_FRIST_MS = 3 * 24 * 60 * 60 * 1000;
/** Eine Kopie mit offenen Fragen frischt der Abgleich auf, wenn ihre Fragen älter sind (eine neue Antwort ändert die Zahl nicht). */
export const FRAGEN_FRISCH_MS = 5 * 60_000;
/** Höchstens so oft frischt das Öffnen einer gesendeten Besichtigung sie auf. */
export const AUFFRISCHEN_TAKT = 30_000;

export interface AbgleichErgebnis {
  angelegt: number;
  /** Zum ersten Mal nicht mehr in der Liste – vorgemerkt, noch nicht gelöscht. */
  vermisst: number;
  /** Nach drei Tagen „vermisst“ vom Gerät genommen (der Rückfall). */
  geloescht: number;
  /** Vom Büro gelöscht (die Liste nennt die Kennung) – sofort vom Gerät genommen, mit Hinweis (seit E3c, S6). */
  vomBueroGeloescht: number;
}

/**
 * Ein Abgleich mit dem Server – Fehler wirft er weiter (`einsichtAnstossen`
 * fängt sie). Vorher: Was schon im Büro ist, aber noch Dateien am Gerät hat
 * (die App ging mitten im Freigeben zu), gibt sie jetzt frei.
 */
export async function einsichtAbgleichen(): Promise<AbgleichErgebnis> {
  const amGeraet = await speicher.aufnahmen.toArray();
  const mitDateien = await aufnahmenMitDateien();
  for (const a of amGeraet) if (a.stand === "im_buero" && mitDateien.has(a.id)) await dateienFreigeben(a.id);

  // Was danach erst „im Büro“ wird, kennt die Liste womöglich noch nicht – das bleibt.
  const gefragtAm = new Date().toISOString();
  const antwort = await holen<GesendetAntwort>("/api/doku/aufnahmen");
  // Keine Liste (leerer Körper, fremdes JSON): nichts abgleichen – eine leere Antwort ist keine leere Liste.
  if (!Array.isArray(antwort?.aufnahmen)) throw new Error("Die Liste „Gesendet“ kam ohne Inhalt – nichts abgeglichen.");
  const liste: GesendetZeile[] = antwort.aufnahmen.filter((z) => istKennung(z?.id));
  const amServer = new Set(liste.map((z) => z.id));
  const hier = new Map(amGeraet.map((a) => [a.id, a]));

  // Das Büro hat gelöscht (seit E3c, S6): die Kopie sofort weg, in jedem Stand, samt Postausgang – mit Hinweis.
  // Ein Server mit altem Stand schickt das Feld nicht; dann greift unten der Rückfall.
  let vomBuero = 0;
  const geloeschtAmServer = Array.isArray(antwort.geloescht) ? antwort.geloescht.filter((id) => istKennung(id)) : [];
  for (const id of geloeschtAmServer) {
    if (!hier.has(id)) continue;
    if (await vomBueroGeloescht(id, new Date(gefragtAm))) vomBuero += 1;
    hier.delete(id);
  }

  let angelegt = 0;
  for (const zeile of liste.filter((z) => !hier.has(z.id)).slice(0, KOPIEN_JE_ABGLEICH)) {
    const vorher = new Date().toISOString();
    const ganz = await ganzHolen(zeile.id);
    if (ganz && (await kopieAblegen(ganz, vorher))) angelegt += 1;
  }

  // Fragen und Termin auffrischen, wo sich laut Liste etwas getan hat (seit E3).
  const jetzt = Date.parse(gefragtAm);
  const auffrischen = liste.filter((z) => {
    const a = hier.get(z.id);
    return a !== undefined && fragenVeraltet(a, z, jetzt);
  });
  for (const zeile of auffrischen.slice(0, KOPIEN_JE_ABGLEICH)) {
    const vorher = new Date().toISOString();
    const ganz = await ganzHolen(zeile.id);
    if (ganz) await kopieAuffrischen(ganz, vorher);
  }

  let vermisst = 0;
  let geloescht = 0;
  for (const a of amGeraet) {
    if (a.stand !== "im_buero" || !hier.has(a.id)) continue;
    if (amServer.has(a.id)) {
      // Wieder (oder weiter) in der Liste: eine Vormerkung gilt nicht mehr.
      if (a.vermisstSeit) await speicher.aufnahmen.update(a.id, { vermisstSeit: null });
      continue;
    }
    if (!a.vermisstSeit) {
      if (await vermisstMarkieren(a.id, gefragtAm)) vermisst += 1;
    } else if (Date.parse(gefragtAm) - Date.parse(a.vermisstSeit) >= KOPIE_FRIST_MS && (await kopieLoeschen(a.id, gefragtAm))) {
      geloescht += 1;
    }
  }
  return { angelegt, vermisst, geloescht, vomBueroGeloescht: vomBuero };
}

/**
 * Eine Besichtigung ganz vom Server – null, wenn es sie (für mich) nicht
 * mehr gibt (404) oder die Antwort nicht zu ihr passt. Andere Fehler wirft
 * es weiter.
 */
async function ganzHolen(id: string): Promise<AufnahmeAmServer | null> {
  let ganz: AufnahmeAmServer;
  try {
    ganz = await holen<AufnahmeAmServer>(`/api/doku/aufnahme/${id}`);
  } catch (fehler) {
    // Inzwischen weg (oder nicht meine): einfach weiter.
    if (fehler instanceof ApiFehler && fehler.status === 404) return null;
    throw fehler;
  }
  return ganz?.id === id ? ganz : null;
}

/**
 * Sind die Fragen der Kopie nicht mehr, was die Liste sagt? Nur an einer
 * gesendeten Kopie: wenn die Zahl offener Fragen nicht mehr die vom letzten
 * Auffrischen ist – oder wenn welche offen sind und das Auffrischen länger
 * als `FRAGEN_FRISCH_MS` her ist (eine neue Antwort des Büros ändert die
 * Zahl nicht). Ohne Zahl in der Liste (älterer Server): keine.
 */
export function fragenVeraltet(a: Pick<Aufnahme, "stand" | "offeneFragenAmServer" | "fragenAm">, z: Pick<GesendetZeile, "offeneFragen">, jetzt: number): boolean {
  if (a.stand === "in_aufnahme") return false;
  const offen = typeof z.offeneFragen === "number" ? z.offeneFragen : 0;
  if (offen !== (a.offeneFragenAmServer ?? 0)) return true;
  return offen > 0 && (!a.fragenAm || jetzt - Date.parse(a.fragenAm) >= FRAGEN_FRISCH_MS);
}

/** Wie viele dieser Fragen offen sind – so zählt auch die Liste „Gesendet“. */
function offeneZahl(s: Pick<AufnahmeAmServer, "fragen">): number {
  return (s.fragen ?? []).filter((f) => f.stand === "offen").length;
}

/**
 * Eine Kopie mit dem auffrischen, was sich nach dem Senden ändern kann (seit
 * E3): die Fragen samt Antworten und der Montagetermin – und bei einer, die
 * im Büro ist, Name und Adresse (die berichtigt das Büro; der Verkäufer
 * ändert sie nach dem Senden nie). Seit E3b (O70) auch die Dachplanung, wenn
 * das Büro sie angehängt hat – solange am Gerät für sie nichts wartet. Die
 * Stücke der Besichtigung bleiben, wie sie sind. Was das Gerät zu den Fragen gesagt hat, wird aufgeräumt, sobald
 * die Kopie es sicher kennt (gespraech.ts, `eigenesAufraeumen`) – dafür
 * zählt `gefragtAm`, die Zeit kurz vor der Anfrage. Nur an einer Kopie, die
 * gesendet ist. Gibt zurück, ob aufgefrischt wurde.
 */
export async function kopieAuffrischen(s: AufnahmeAmServer, gefragtAm: string): Promise<boolean> {
  return speicher.transaction("rw", speicher.aufnahmen, speicher.postausgang, speicher.k2, async () => {
    const a = await speicher.aufnahmen.get(s.id);
    if (!a || a.stand === "in_aufnahme") return false;
    const wartend = new Set((await speicher.postausgang.where("aufnahmeId").equals(a.id).toArray()).map((e) => `${e.art}:${e.kennung}`));
    const fragen = Array.isArray(s.fragen) ? s.fragen : [];
    const aenderung: Partial<Aufnahme> = {
      fragen,
      fragenAm: gefragtAm,
      offeneFragenAmServer: offeneZahl({ fragen }),
      montagetermin: { am: s.anlage.montagetermin?.am ?? null, text: s.anlage.montagetermin?.text ?? null },
      ...eigenesAufraeumen(a, fragen, gefragtAm, (art, kennung) => wartend.has(`${art}:${kennung}`)),
    };
    if (a.stand === "im_buero") {
      aenderung.kunde = { ...s.kunde };
      aenderung.objekt = { ...s.objekt };
    }
    await speicher.aufnahmen.update(a.id, aenderung);
    // Die Dachplanung aus dem Büro (seit E3b): Hängt am Server ein PDF, das das Gerät nicht kennt, und wartet am Gerät
    // nichts zur Dachplanung, gilt das vom Server – ohne Bytes, „PDF ansehen“ holt es von dort. Seit E3c (S5) ebenso
    // der Excel-Report (das Büro kann ihn auch anhängen).
    const k = s.anlage.k2;
    if (k && !wartend.has(`k2:${s.anlage.id}`)) {
      const vorher = await speicher.k2.get(s.anlage.id);
      let neu = vorher;
      if (k.pruefsumme && vorher?.pruefsumme !== k.pruefsumme) neu = { ...k2VomServer(s.anlage.id, a.id, k), excel: vorher?.excel ?? null };
      if (k.excel && neu?.excel?.pruefsumme !== k.excel.pruefsumme) neu = { ...(neu ?? { ...k2VomServer(s.anlage.id, a.id, k), excel: null }), excel: excelVomServer(k.excel) };
      if (neu && neu !== vorher) await speicher.k2.put(neu);
    }
    return true;
  });
}

/** Die Dachplanung, wie sie am Server liegt – ohne Bytes („PDF ansehen“ holt das PDF von dort); ohne Excel-Report. */
function k2VomServer(anlageId: string, aufnahmeId: string, k: Pick<AufnahmeAmServer["anlage"]["k2"], "pruefsumme" | "groesse" | "uebersprungen">): Omit<K2, "excel"> {
  return {
    id: anlageId,
    aufnahmeId,
    pdf: null,
    dateiname: null,
    groesse: k.groesse ?? 0,
    pruefsumme: k.pruefsumme,
    uebersprungen: k.pruefsumme === null && k.uebersprungen,
    nachtragId: null,
    hochgeladen: k.pruefsumme !== null,
  };
}

/** Der Excel-Report, wie er am Server liegt (seit E3c, S5) – ohne Bytes. */
function excelVomServer(x: { pruefsumme: string; groesse: number | null }): K2Excel {
  return { daten: null, dateiname: null, groesse: x.groesse ?? 0, pruefsumme: x.pruefsumme, hochgeladen: true };
}

/**
 * Zum ersten Mal nicht in der Liste: vormerken, seit wann – nach denselben
 * Regeln wie das Löschen: nur eine, die im Büro ist, schon bevor die Liste
 * erfragt wurde (`vor`), für die nichts mehr wartet und an der nicht gerade
 * nachgereicht wird (solange redet das Gerät ohnehin mit dem Server über
 * sie). Gibt zurück, ob vorgemerkt wurde.
 */
async function vermisstMarkieren(aufnahmeId: string, vor: string): Promise<boolean> {
  return speicher.transaction("rw", speicher.aufnahmen, speicher.postausgang, speicher.nachtraege, async () => {
    const a = await speicher.aufnahmen.get(aufnahmeId);
    if (a?.stand !== "im_buero" || a.vermisstSeit || (a.imBueroAm !== null && a.imBueroAm >= vor)) return false;
    if ((await speicher.postausgang.where("aufnahmeId").equals(aufnahmeId).count()) > 0) return false;
    if ((await speicher.nachtraege.where("aufnahmeId").equals(aufnahmeId).filter((n) => n.stand !== "im_buero").count()) > 0) return false;
    await speicher.aufnahmen.update(aufnahmeId, { vermisstSeit: vor });
    return true;
  });
}

/**
 * Eine Besichtigung vom Server als Nur-lesen-Kopie ablegen: Stand „im
 * Büro“, Fotos und Memos gelten als hochgeladen und gesendet, die
 * Dachplanung ohne PDF-Bytes, die Nachträge im Büro mit dem, was sie
 * gebracht haben (seit Schritt 5), die Fragen und der Montagetermin (seit
 * E3; `gefragtAm`: kurz vor der Anfrage). Nur, wenn es sie am Gerät noch
 * nicht gibt.
 */
export async function kopieAblegen(s: AufnahmeAmServer, gefragtAm = new Date().toISOString()): Promise<boolean> {
  const gesendet = s.gesendetAm ?? s.begonnenAm;
  // Ein Nachtrag, der am Server noch offen ist, gehört dem Gerät, das ihn begonnen hat – nicht in die Kopie.
  const nochOffen = new Set(s.nachtraege.flatMap((n) => (n.gesendetAm ? [] : [n.id])));
  const imBuero = <T extends { nachtragId: string | null }>(liste: T[]) => liste.filter((x) => x.nachtragId === null || !nochOffen.has(x.nachtragId));
  const aufnahme: Aufnahme = {
    id: s.id,
    kunde: { ...s.kunde },
    objekt: { ...s.objekt },
    anlage: { id: s.anlage.id, produktlinie: s.anlage.produktlinie },
    begonnenAm: s.begonnenAm,
    stand: "im_buero",
    fehler: null,
    abgeschlossenAm: gesendet,
    imBueroAm: gesendet,
    fragen: Array.isArray(s.fragen) ? s.fragen : [],
    fragenAm: gefragtAm,
    offeneFragenAmServer: offeneZahl(s),
    montagetermin: { am: s.anlage.montagetermin?.am ?? null, text: s.anlage.montagetermin?.text ?? null },
  };
  const stationen: Station[] = s.stationen.map((x) => ({
    id: x.id,
    aufnahmeId: s.id,
    art: x.art,
    nr: x.nr,
    fertig: x.fertig,
    sonstiges: x.sonstiges,
    geaendertImNachtrag: x.geaendertImNachtrag,
    // Seit E3c (S5): Höhe und Zugang am Dach – damit „Gesendet“ sie zeigt und ein Nachtrag sie berichtigen kann.
    // Ein Server mit altem Stand schickt sie nicht: dann bleiben sie unbekannt (und gehen nie leer hinaus).
    ...(x.fachfragen !== undefined ? { fachfragen: x.fachfragen } : {}),
  }));
  const fotos: Foto[] = imBuero(s.fotos).map((x) => ({
    id: x.id,
    aufnahmeId: s.id,
    stationId: x.stationId,
    reihenfolge: x.reihenfolge,
    pruefsumme: x.pruefsumme,
    breite: x.breite,
    hoehe: x.hoehe,
    groesse: x.groesse,
    aufnahmezeit: x.aufnahmezeit,
    zeichnung: x.zeichnung,
    nachtragId: x.nachtragId,
    hochgeladen: true,
    abgeschickt: true,
  }));
  const notizen: Notiz[] = imBuero(s.notizen).map((x) => ({ ...x, aufnahmeId: s.id }));
  const memos: Memo[] = imBuero(s.memos).map((x) => ({
    id: x.id,
    aufnahmeId: s.id,
    notizId: x.notizId,
    pruefsumme: x.pruefsumme,
    format: x.format,
    // Als Zahl – auch, falls die Datenbank sie als Text liefert (numeric).
    dauerSekunden: Number(x.dauerSekunden),
    aufgenommenAm: x.aufgenommenAm,
    nachtragId: x.nachtragId,
    hochgeladen: true,
    abgeschickt: true,
  }));
  const laengen: Laenge[] = imBuero(s.laengen).map((x) => ({
    id: x.id,
    aufnahmeId: s.id,
    stationId: x.stationId,
    laengenArt: x.laengenArt,
    meter: Number(x.meter),
    anmerkung: x.anmerkung,
    nachtragId: x.nachtragId,
    geaendertImNachtrag: x.geaendertImNachtrag,
  }));
  // Seit E3b: das Material von draußen – ein Server mit altem Stand schickt die Liste nicht, dann keines.
  const material: Materialangabe[] = imBuero(Array.isArray(s.material) ? s.material : []).map((x, nr) => ({
    id: x.id,
    aufnahmeId: s.id,
    stationId: x.stationId,
    standardId: x.standardId,
    artikelnummer: x.artikelnummer,
    bezeichnung: x.bezeichnung,
    menge: Number(x.menge),
    einheit: x.einheit,
    nachtragId: x.nachtragId,
    geaendertImNachtrag: x.geaendertImNachtrag,
    nr,
  }));
  // Die Dachplanung ohne Bytes – seit E3c (S5) mit dem Excel-Report; ein Server mit altem Stand schickt ihn nicht.
  const k = s.anlage.k2;
  const k2: K2 | null = k.pruefsumme !== null || k.uebersprungen || k.excel ? { ...k2VomServer(s.anlage.id, s.id, k), excel: k.excel ? excelVomServer(k.excel) : null } : null;
  // Nur, was schon im Büro ist – ein offener Nachtrag (auf einem anderen Gerät begonnen) gehört dorthin.
  const nachtraege: Nachtrag[] = s.nachtraege.flatMap((n) =>
    n.gesendetAm
      ? [{ id: n.id, aufnahmeId: s.id, begonnenAm: n.begonnenAm, stand: "im_buero" as const, abgeschlossenAm: n.gesendetAm, imBueroAm: n.gesendetAm, abgeschickt: true }]
      : [],
  );

  const tabellen = [speicher.aufnahmen, ...stueckeTabellen(), speicher.k2, speicher.nachtraege];
  return speicher.transaction("rw", tabellen, async () => {
    if (await speicher.aufnahmen.get(s.id)) return false;
    await speicher.aufnahmen.add(aufnahme);
    await speicher.stationen.bulkPut(stationen);
    await speicher.notizen.bulkPut(notizen);
    await speicher.laengen.bulkPut(laengen);
    await speicher.materialangaben.bulkPut(material);
    await speicher.fotos.bulkPut(fotos);
    await speicher.memos.bulkPut(memos);
    if (k2) await speicher.k2.put(k2);
    await speicher.nachtraege.bulkPut(nachtraege);
    return true;
  });
}

/**
 * Eine Nur-lesen-Kopie vom Gerät löschen – samt allem, was dazugehört, und
 * den Dateien, die sonst niemand braucht. Der Abgleich ruft es erst, wenn
 * die Kopie drei Tage lang vermisst war (`KOPIE_FRIST_MS`). Nur eine, die im
 * Büro ist, schon bevor die Liste erfragt wurde (`vor`), und für die nichts
 * mehr im Postausgang wartet. Nie eine, die aufgenommen wird oder abgeschlossen ist
 * – und nie eine, an der etwas nachgereicht wird (seit Schritt 5: ein
 * Nachtrag offen oder auf dem Weg; ist schon alles davon gesendet, wartet
 * im Postausgang womöglich nichts mehr).
 */
export async function kopieLoeschen(aufnahmeId: string, vor: string): Promise<boolean> {
  return speicher.transaction("rw", wegraeumTabellen(), async () => {
    const a = await speicher.aufnahmen.get(aufnahmeId);
    if (a?.stand !== "im_buero" || (a.imBueroAm !== null && a.imBueroAm >= vor)) return false;
    if ((await speicher.postausgang.where("aufnahmeId").equals(aufnahmeId).count()) > 0) return false;
    if ((await speicher.nachtraege.where("aufnahmeId").equals(aufnahmeId).filter((n) => n.stand !== "im_buero").count()) > 0) return false;
    // Das Wegräumen selbst steht seit E3c (S6) an einer Stelle (wegraeumen.ts).
    await besichtigungWegraeumen(aufnahmeId);
    return true;
  });
}

// --- Anstoßen: beim Öffnen der Startseite und wenn das Netz zurückkommt -------------------

let zuletzt = 0;
let laeuft = false;

/**
 * Stößt einen Abgleich an – nicht öfter als etwa einmal je Minute, nie zwei
 * auf einmal, ohne Netz gar nicht. Ging er am Empfang schief, darf der
 * nächste Anstoß (Netz zurück) gleich wieder.
 */
export function einsichtAnstossen(jetzt = Date.now()): void {
  if (laeuft || ohneNetz() || jetzt - zuletzt < EINSICHT_TAKT) return;
  zuletzt = jetzt;
  laeuft = true;
  void einsichtAbgleichen()
    .catch((fehler: unknown) => {
      if (fehler instanceof OhneVerbindung) {
        zuletzt = 0;
        return;
      }
      // Eine Abweisung des Servers (nicht angemeldet o. Ä.) klärt die Anmeldung – still.
      if (fehler instanceof ApiFehler) return;
      fehlerMelden("doku", new Error(`Abgleich „Gesendet“ fehlgeschlagen – ${fehler instanceof Error ? fehler.name : "unbekannt"}`));
    })
    .finally(() => {
      laeuft = false;
    });
}

/** Je Besichtigung: wann zuletzt beim Öffnen aufgefrischt – und welche gerade. */
const zuletztAufgefrischt = new Map<string, number>();
const frischtAuf = new Set<string>();

/**
 * Eine gesendete Besichtigung auffrischen, weil sie gerade angesehen wird
 * (seit E3): Fragen, Termin, Name und Adresse. Nicht öfter als etwa alle 30
 * Sekunden je Besichtigung, ohne Netz gar nicht; Fehler bleiben still wie
 * beim Abgleich. Gibt es sie am Server nicht (mehr), tut es nichts – das
 * klärt der Abgleich der Startseite.
 */
export function auffrischenAnstossen(aufnahmeId: string, jetzt = Date.now()): void {
  if (frischtAuf.has(aufnahmeId) || ohneNetz() || jetzt - (zuletztAufgefrischt.get(aufnahmeId) ?? 0) < AUFFRISCHEN_TAKT) return;
  zuletztAufgefrischt.set(aufnahmeId, jetzt);
  frischtAuf.add(aufnahmeId);
  const vorher = new Date().toISOString();
  void ganzHolen(aufnahmeId)
    .then((ganz) => (ganz ? kopieAuffrischen(ganz, vorher) : false))
    .catch((fehler: unknown) => {
      if (fehler instanceof OhneVerbindung) {
        zuletztAufgefrischt.delete(aufnahmeId);
        return;
      }
      if (fehler instanceof ApiFehler) return;
      fehlerMelden("doku", new Error(`Auffrischen einer Besichtigung fehlgeschlagen – ${fehler instanceof Error ? fehler.name : "unbekannt"}`));
    })
    .finally(() => frischtAuf.delete(aufnahmeId));
}

// --- Für die Liste „Gesendet“ ----------------------------------------------------------------

/**
 * Wann, für Menschen: „heute 10:42“, „gestern 16:05“, sonst das Datum –
 * „12. Okt.“, aus einem anderen Jahr „12. Okt. 2025“.
 */
export function wannZeigen(iso: string, jetzt = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const uhrzeit = d.toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" });
  const tag = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const tage = Math.round((tag(jetzt) - tag(d)) / 86_400_000);
  if (tage === 0) return `heute ${uhrzeit}`;
  if (tage === 1) return `gestern ${uhrzeit}`;
  return d.toLocaleDateString("de-AT", { day: "numeric", month: "short", ...(d.getFullYear() !== jetzt.getFullYear() ? { year: "numeric" } : {}) });
}
